/**
 * HTML to Virtual DOM Parser
 *
 * Converts HTML strings to virtual DOM trees using htmlparser2 for parsing.
 * This implementation replaces the unmaintained html-to-v package while
 * maintaining full API compatibility.
 *
 * Based on React's HTML DOM property configuration and HTML parser libraries.
 */

import type { Element as DomElement } from "domhandler";

import * as htmlparser2 from "htmlparser2";
import { decode } from "html-entities";

import { VNode, VText } from "../vdom/index.js";

/* -------------------------------------------------------------------------- */
/* Type definitions inferred for the parser                                       */
/* -------------------------------------------------------------------------- */
interface PropInfo {
  attributeName: string;
  propertyName?: string;
  mustUseAttribute?: boolean;
  mustUseProperty?: boolean;
  hasBooleanValue?: boolean;
  hasNumericValue?: boolean;
  hasPositiveNumericValue?: boolean;
  hasOverloadedBooleanValue?: boolean;
  isCustomAttribute?: boolean;
}

type VNodeProperties = {
  attributes: Record<string, string>;
  [key: string]: any;
};

type PropertySetter = {
  set: (properties: VNodeProperties, propInfo: PropInfo, value: any) => void;
};

// ============================================================================
// Property Info System
// Configuration from the old virtual DOM library (originally from React's HTMLDOMPropertyConfig)
// This distinguishes HTML properties from attributes for correct VNode generation
// ============================================================================

// Property masks for attribute/property classification

const MUST_USE_ATTRIBUTE: number = 0x1;
const MUST_USE_PROPERTY: number = 0x2;
const HAS_BOOLEAN_VALUE: number = 0x4;
const HAS_NUMERIC_VALUE: number = 0x8;
const HAS_POSITIVE_NUMERIC_VALUE: number = 0x10 | 0x8;
const HAS_OVERLOADED_BOOLEAN_VALUE: number = 0x20;

// HTML DOM properties configuration

const Properties: Record<string, number | null> = {
  accept: null,
  acceptCharset: null,
  accessKey: null,
  action: null,
  allowFullScreen: MUST_USE_ATTRIBUTE | HAS_BOOLEAN_VALUE,
  allowTransparency: MUST_USE_ATTRIBUTE,
  alt: null,
  async: HAS_BOOLEAN_VALUE,
  autoComplete: null,
  autoFocus: HAS_BOOLEAN_VALUE,
  autoPlay: HAS_BOOLEAN_VALUE,
  capture: MUST_USE_ATTRIBUTE | HAS_BOOLEAN_VALUE,
  cellPadding: null,
  cellSpacing: null,
  charSet: MUST_USE_ATTRIBUTE,
  challenge: MUST_USE_ATTRIBUTE,
  checked: MUST_USE_PROPERTY | HAS_BOOLEAN_VALUE,
  classID: MUST_USE_ATTRIBUTE,
  className: MUST_USE_ATTRIBUTE,
  cols: MUST_USE_ATTRIBUTE | HAS_POSITIVE_NUMERIC_VALUE,
  colSpan: null,
  content: null,
  contentEditable: null,
  contextMenu: MUST_USE_ATTRIBUTE,
  controls: MUST_USE_PROPERTY | HAS_BOOLEAN_VALUE,
  coords: null,
  crossOrigin: null,
  data: null,
  dateTime: MUST_USE_ATTRIBUTE,
  defer: HAS_BOOLEAN_VALUE,
  dir: null,
  disabled: MUST_USE_ATTRIBUTE | HAS_BOOLEAN_VALUE,
  download: HAS_OVERLOADED_BOOLEAN_VALUE,
  draggable: null,
  encType: null,
  form: MUST_USE_ATTRIBUTE,
  formAction: MUST_USE_ATTRIBUTE,
  formEncType: MUST_USE_ATTRIBUTE,
  formMethod: MUST_USE_ATTRIBUTE,
  formNoValidate: HAS_BOOLEAN_VALUE,
  formTarget: MUST_USE_ATTRIBUTE,
  frameBorder: MUST_USE_ATTRIBUTE,
  headers: null,
  height: MUST_USE_ATTRIBUTE,
  hidden: MUST_USE_ATTRIBUTE | HAS_BOOLEAN_VALUE,
  high: null,
  href: null,
  hrefLang: null,
  htmlFor: null,
  httpEquiv: null,
  icon: null,
  id: MUST_USE_PROPERTY,
  is: MUST_USE_ATTRIBUTE,
  keyParams: MUST_USE_ATTRIBUTE,
  keyType: MUST_USE_ATTRIBUTE,
  label: null,
  lang: null,
  list: MUST_USE_ATTRIBUTE,
  loop: MUST_USE_PROPERTY | HAS_BOOLEAN_VALUE,
  low: null,
  manifest: MUST_USE_ATTRIBUTE,
  marginHeight: null,
  marginWidth: null,
  max: null,
  maxLength: MUST_USE_ATTRIBUTE,
  media: MUST_USE_ATTRIBUTE,
  mediaGroup: null,
  method: null,
  min: null,
  minLength: MUST_USE_ATTRIBUTE,
  multiple: MUST_USE_PROPERTY | HAS_BOOLEAN_VALUE,
  muted: MUST_USE_PROPERTY | HAS_BOOLEAN_VALUE,
  name: null,
  noValidate: HAS_BOOLEAN_VALUE,
  open: HAS_BOOLEAN_VALUE,
  optimum: null,
  pattern: null,
  placeholder: null,
  poster: null,
  preload: null,
  radioGroup: null,
  readOnly: MUST_USE_PROPERTY | HAS_BOOLEAN_VALUE,
  rel: null,
  required: HAS_BOOLEAN_VALUE,
  role: MUST_USE_ATTRIBUTE,
  rows: MUST_USE_ATTRIBUTE | HAS_POSITIVE_NUMERIC_VALUE,
  rowSpan: null,
  sandbox: null,
  scope: null,
  scoped: HAS_BOOLEAN_VALUE,
  scrolling: null,
  seamless: MUST_USE_ATTRIBUTE | HAS_BOOLEAN_VALUE,
  selected: MUST_USE_PROPERTY | HAS_BOOLEAN_VALUE,
  shape: null,
  size: MUST_USE_ATTRIBUTE | HAS_POSITIVE_NUMERIC_VALUE,
  sizes: MUST_USE_ATTRIBUTE,
  span: HAS_POSITIVE_NUMERIC_VALUE,
  spellCheck: null,
  src: null,
  srcDoc: MUST_USE_PROPERTY,
  srcSet: MUST_USE_ATTRIBUTE,
  start: HAS_NUMERIC_VALUE,
  step: null,
  style: null,
  tabIndex: null,
  target: null,
  title: null,
  type: null,
  useMap: null,
  value: MUST_USE_PROPERTY,
  width: MUST_USE_ATTRIBUTE,
  wmode: MUST_USE_ATTRIBUTE,
  autoCapitalize: null,
  autoCorrect: null,
  itemProp: MUST_USE_ATTRIBUTE,
  itemScope: MUST_USE_ATTRIBUTE | HAS_BOOLEAN_VALUE,
  itemType: MUST_USE_ATTRIBUTE,
  itemID: MUST_USE_ATTRIBUTE,
  itemRef: MUST_USE_ATTRIBUTE,
  property: null,
  unselectable: MUST_USE_ATTRIBUTE,
};

const PropertyToAttributeMapping: Record<string, string> = {
  className: "class",
  htmlFor: "for",
  httpEquiv: "http-equiv",
  acceptCharset: "accept-charset",
};

function checkMask(value: number | null, bitmask: number): boolean {
  if (value == null) return false;

  return (value & bitmask) === bitmask;
}

// Build property info lookup table
const propInfoByAttributeName: Record<string, PropInfo> = {};

Object.keys(Properties).forEach((propName) => {
  const propConfig = Properties[propName];
  const attributeName =
    PropertyToAttributeMapping[propName] || propName.toLowerCase();

  const propertyInfo = {
    attributeName,
    propertyName: propName,
    mustUseAttribute: checkMask(propConfig, MUST_USE_ATTRIBUTE),
    mustUseProperty: checkMask(propConfig, MUST_USE_PROPERTY),
    hasBooleanValue: checkMask(propConfig, HAS_BOOLEAN_VALUE),
    hasNumericValue: checkMask(propConfig, HAS_NUMERIC_VALUE),
    hasPositiveNumericValue: checkMask(propConfig, HAS_POSITIVE_NUMERIC_VALUE),
    hasOverloadedBooleanValue: checkMask(
      propConfig,
      HAS_OVERLOADED_BOOLEAN_VALUE,
    ),
  };

  propInfoByAttributeName[attributeName] = propertyInfo;
});

function getPropertyInfo(attributeName: string): PropInfo {
  const lowerCased = attributeName.toLowerCase();

  if (
    Object.prototype.hasOwnProperty.call(propInfoByAttributeName, lowerCased)
  ) {
    return propInfoByAttributeName[lowerCased];
  }

  // Custom attribute
  return {
    attributeName,
    mustUseAttribute: true,
    isCustomAttribute: true,
  } as PropInfo;
}

// ============================================================================
// Property Setters
// ============================================================================

/**
 * Parse CSS style string into object
 */
function parseStyles(input: string): Record<string, string> {
  const attributes = input.split(";");
  const styles = attributes.reduce(
    (object: Record<string, string>, attribute) => {
      const entry = attribute.split(/:(.*)/);

      if (entry[0] && entry[1]) {
        object[entry[0].trim()] = entry[1].trim();
      }

      return object;
    },
    {},
  );

  return styles;
}

const propertyValueConversions: Record<string, (value: string) => any> = {
  style: parseStyles,
  placeholder: decode,
  title: decode,
  alt: decode,
};

function propertyIsTrue(propInfo: PropInfo, value: string): boolean {
  if (propInfo.hasBooleanValue) {
    return value === "" || value.toLowerCase() === propInfo.attributeName;
  }
  if (propInfo.hasOverloadedBooleanValue) {
    return value === "";
  }

  return false;
}

function getPropertyValue(propInfo: PropInfo, value: string): any {
  const isTrue = propertyIsTrue(propInfo, value);

  if (propInfo.hasBooleanValue) {
    return !!isTrue;
  }
  if (propInfo.hasOverloadedBooleanValue) {
    return isTrue ? true : value;
  }
  if (propInfo.hasNumericValue || propInfo.hasPositiveNumericValue) {
    return Number(value);
  }

  return value;
}

function setVNodeProperty(
  properties: VNodeProperties,
  propInfo: PropInfo,
  value: any,
): void {
  const propName = propInfo.propertyName;
  let valueConverter: ((v: string) => any) | undefined;

  if (
    propName &&
    Object.prototype.hasOwnProperty.call(propertyValueConversions, propName)
  ) {
    valueConverter = propertyValueConversions[propInfo.propertyName as string];

    value = valueConverter(value);
  }

  properties[propInfo.propertyName as string] = getPropertyValue(
    propInfo,
    value,
  );
}

function getAttributeValue(propInfo: PropInfo, value: string): string {
  if (propInfo.hasBooleanValue) {
    return "";
  }

  return value;
}

function setVNodeAttribute(
  properties: VNodeProperties,
  propInfo: PropInfo,
  value: any,
): void {
  properties.attributes[propInfo.attributeName] = getAttributeValue(
    propInfo,
    value,
  );
}

function getPropertySetter(propInfo: PropInfo): PropertySetter {
  if (propInfo.mustUseAttribute) {
    return { set: setVNodeAttribute };
  }

  return { set: setVNodeProperty };
}

/**
 * Convert tag attributes to VNode properties
 */
function convertTagAttributes(tag: DomElement): VNodeProperties {
  const attributes = tag.attribs as Record<string, string>;
  const vNodeProperties: VNodeProperties = {
    attributes: {},
  };

  Object.keys(attributes).forEach((attributeName) => {
    const value = attributes[attributeName];
    const propInfo = getPropertyInfo(attributeName);
    const propertySetter = getPropertySetter(propInfo);

    propertySetter.set(vNodeProperties, propInfo, value);
  });

  return vNodeProperties;
}

// ============================================================================
// HTML Parser to VDOM Converter
// ============================================================================

function createConverter(VNodeClass: typeof VNode, VTextClass: typeof VText) {
  const converter: {
    convert: (node: any, getVNodeKey?: (attrs: VNodeProperties) => any) => any;
    convertTag: (
      tag: any,
      getVNodeKey?: (attrs: VNodeProperties) => any,
    ) => any;
  } = {
    convert(node: any, getVNodeKey?: (attrs: VNodeProperties) => any) {
      if (
        node.type === "tag" ||
        node.type === "script" ||
        node.type === "style"
      ) {
        return converter.convertTag(node, getVNodeKey);
      }
      if (node.type === "text") {
        return new VTextClass(decode(node.data));
      }

      // Converting an unsupported node, return an empty text node instead
      return new VTextClass("");
    },

    convertTag(tag: any, getVNodeKey?: (attrs: VNodeProperties) => any) {
      const attributes = convertTagAttributes(tag);
      let key: any;

      if (getVNodeKey) {
        key = getVNodeKey(attributes);
      }

      const children = Array.prototype.map.call(
        tag.children || [],
        (node: any) => converter.convert(node, getVNodeKey),
      );

      // Instrumentation: Detect malformed table rows
      if (tag.name === "tr") {
        const hasVText = children.some(
          (child: any) => child.type === "VirtualText",
        );

        if (hasVText) {
          console.warn(
            "[html-parser] Malformed <tr>: Contains VText children (without <td>/<th> wrapper)",
            {
              tagName: tag.name,
              childrenTypes: children.map((c: any) => ({
                type: c.type,
                content:
                  c.type === "VirtualText" ? c.text?.substring(0, 50) : c.tag,
              })),
              parentTagName: tag.parent?.name,
            },
          );
        }
      }

      // Instrumentation: Detect SVG/Mermaid content in unusual places
      if (tag.name === "tr" || tag.name === "td" || tag.name === "th") {
        const hasSvg = children.some(
          (child: any) =>
            child.type === "VirtualNode" && child.tagName === "svg",
        );

        if (hasSvg) {
          console.warn(
            "[html-parser] SVG found directly in table cell (should be in <img> or wrapped)",
            {
              tagName: tag.name,
              hasNestedSvg: true,
              parent: tag.parent?.name,
            },
          );
        }
      }

      return new VNodeClass(tag.name, attributes, children, key, undefined);
    },
  };

  return converter;
}

/**
 * Parse HTML string into DOM nodes
 *
 * NOTE: htmlparser2 v10.0.0 auto-decodes entities by default.
 * We set decodeEntities: false to match v3.9.0 behavior,
 * then manually decode using html-entities.
 */
function parseHTML(html: string): any[] {
  const handler = new htmlparser2.DomHandler();
  const parser = new htmlparser2.Parser(handler, {
    lowerCaseAttributeNames: false,
    decodeEntities: false, // Required for htmlparser2 v10.0.0 compatibility
  });

  parser.parseComplete(html);

  return handler.dom as any[];
}

/**
 * Main converter function
 */
function convertHTML(options: any, html?: string): any {
  // Support both (options, html) and (html) signatures
  let opts: any = options;
  let htmlString: string = html as string;

  if (typeof options === "string") {
    htmlString = options;
    opts = {};
  }

  const converter = createConverter(VNode, VText);
  const tags = parseHTML(htmlString);

  let convertedHTML: any;

  if (tags.length === 0) {
    // Empty HTML
    convertedHTML = new VText("");
  } else if (tags.length > 1) {
    convertedHTML = tags.map((tag: any) =>
      converter.convert(tag, opts.getVNodeKey),
    );
  } else {
    convertedHTML = converter.convert(tags[0], opts.getVNodeKey);
  }

  return convertedHTML;
}

/**
 * Factory function for HTML to VNode conversion
 */
export default function createHTMLtoVDOM(): (
  options: any,
  html?: string,
) => any {
  return convertHTML;
}
